import { type FC, useEffect, useState } from "react";
import { Box, Column, EmptyState, Spinner, HtImage } from "@hightouch/htui";
import { DangerousMarkdownWithHtml } from "src/components/llm/markdown/dangerous-markdown-with-html";
import { CodeBlock } from "src/components/llm/markdown/code-block";
import { useRegionConfig } from "src/utils/use-region-config";
import type { Language } from "src/components/editor";

const IMAGE_EXTENSIONS = new Set(["png", "jpg", "jpeg", "gif", "webp", "svg"]);
const HTML_EXTENSIONS = new Set(["html", "htm"]);
// Extensions rendered as read-only code. Value is the editor language for
// syntax highlighting; undefined renders plain monospace.
const CODE_EXTENSIONS: Record<string, Language | undefined> = {
  json: "json",
  py: undefined,
  csv: undefined,
};

type ArtifactKind = "image" | "html" | "code" | "markdown";

const getExtension = (filename: string): string =>
  filename.split(".").pop()?.toLowerCase() ?? "";

const getArtifactKind = (filename: string): ArtifactKind => {
  const ext = getExtension(filename);
  if (IMAGE_EXTENSIONS.has(ext)) return "image";
  if (HTML_EXTENSIONS.has(ext)) return "html";
  if (ext in CODE_EXTENSIONS) return "code";
  return "markdown";
};

type ArtifactContentProps = {
  filename: string;
  interactionId?: string;
  /** When provided, skips fetching and renders this content directly. */
  content?: string;
  isLoading?: boolean;
};

/**
 * Fetches (unless `content` is provided) and renders a single artifact file —
 * markdown, sandboxed html, code (json/py/csv), or image — filling its flex
 * parent.
 */
export const ArtifactContent: FC<ArtifactContentProps> = ({
  filename,
  interactionId,
  content: providedContent,
  isLoading: providedIsLoading,
}) => {
  const [fetchedContent, setFetchedContent] = useState("");
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [fetchError, setFetchError] = useState(false);
  const [isFetchingContent, setIsFetchingContent] = useState(false);

  const regionConfig = useRegionConfig();

  // Build URL for artifacts (works for both interaction-specific and workspace-wide)
  // Use regional API endpoint to ensure EU workspaces hit the EU API
  const artifactUrl = (() => {
    const params = new URLSearchParams();
    params.set("filename", filename);
    if (interactionId) {
      params.set("interaction_id", interactionId);
    }
    // regionConfig.apiEndpoint already includes /api, so we just need /agents/ad-artifacts
    return `${regionConfig.apiEndpoint}/agents/ad-artifacts?${params.toString()}`;
  })();

  const artifactKind = getArtifactKind(filename);

  useEffect(() => {
    if (providedContent !== undefined) {
      return;
    }

    if (!filename) {
      setFetchedContent("");
      setImageUrl(null);
      return;
    }

    let canceled = false;
    let objectUrl: string | null = null;

    const load = async () => {
      setIsFetchingContent(true);
      setFetchError(false);
      try {
        const res = await fetch(artifactUrl, { credentials: "include" });
        if (!res.ok) {
          throw new Error(`Failed to fetch artifact: ${res.statusText}`);
        }
        if (artifactKind === "image") {
          // Images are binary — render via an object URL instead of reading
          // the response as text.
          const blob = await res.blob();
          if (canceled) return;
          objectUrl = URL.createObjectURL(blob);
          setImageUrl(objectUrl);
        } else {
          const text = await res.text();
          if (!canceled) setFetchedContent(text);
        }
      } catch (error) {
        console.error("Failed to fetch artifact content:", error);
        if (!canceled) {
          setFetchedContent("");
          setImageUrl(null);
          setFetchError(true);
        }
      } finally {
        if (!canceled) setIsFetchingContent(false);
      }
    };

    void load();

    return () => {
      canceled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [artifactUrl, filename, providedContent, artifactKind]);

  const content = providedContent ?? fetchedContent;
  const isLoading = providedIsLoading ?? isFetchingContent;

  if (isLoading) {
    return (
      <Column flex={1} minHeight={0} align="center" justify="center">
        <Spinner size="lg" />
      </Column>
    );
  }

  if (fetchError) {
    return (
      <Box p={6}>
        <EmptyState
          title="Failed to load artifact"
          message="The artifact content could not be fetched"
        />
      </Box>
    );
  }

  if (artifactKind === "html") {
    return (
      <Box
        as="iframe"
        title={filename}
        srcDoc={content}
        sandbox="allow-same-origin"
        flex={1}
        minHeight={0}
        width="100%"
        border="none"
        bg="white"
      />
    );
  }

  if (artifactKind === "code") {
    return (
      <Column flex={1} minHeight={0} overflowY="auto" p={4}>
        <CodeBlock
          value={content}
          language={CODE_EXTENSIONS[getExtension(filename)]}
          showLineNumbers
        />
      </Column>
    );
  }

  if (artifactKind === "image") {
    return (
      <Column flex={1} minHeight={0} overflowY="auto" p={4} align="center">
        {imageUrl && (
          <HtImage
            src={imageUrl}
            alt={filename}
            maxWidth="100%"
            height="auto"
          />
        )}
      </Column>
    );
  }

  return (
    <Column flex={1} minHeight={0} overflowY="auto" p={4}>
      <DangerousMarkdownWithHtml
        useParagraphMargins
        interactionId={interactionId ?? undefined}
      >
        {content}
      </DangerousMarkdownWithHtml>
    </Column>
  );
};
